import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchTextFile } from '../api/resourcesApi';
import remarkGfm from 'remark-gfm';
import ReactMarkdown from 'react-markdown';
import { Typography, Divider } from 'antd';

const { Title, Paragraph } = Typography;

const titleStyles = {
    padding: '16px 0', // Adjusted padding for a cleaner look
    marginBottom: '8px',
    fontWeight: '500',
};

// Styling for the indented block section (e.g., quotes)
const blockQuoteStyles = {
    borderLeft: '4px solid #d9d9d9',
    paddingLeft: '16px',
    marginBottom: '16px',
    backgroundColor: '#f9f9f9',
    borderRadius: '4px',
};

// Main Component
const TextViewer = ({ url }) => {
    const dispatch = useDispatch();

    // Select the text file data from the Redux store
    const textFileData = useSelector((state) => state.resources.textFiles[url]);

    useEffect(() => {
        // Fetch the text file only if it's not already loaded or currently loading
        if (!textFileData || (!textFileData.content && !textFileData.loading)) {
            dispatch(fetchTextFile(url));
        }
    }, [dispatch, url]);

    if (textFileData?.loading) {
        return <div>Loading...</div>;
    }

    if (textFileData?.error) {
        return <div>Error: {textFileData.error}</div>;
    }

    if (textFileData?.content) {
        // Render the markdown content as HTML
        return (
            <div style={{ padding: '16px' }}>
                <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                        h1: ({ children }) => (
                            <Title level={1} style={{ ...titleStyles, fontWeight: '700' }}>
                                {children}
                            </Title>
                        ),
                        h2: ({ children }) => (
                            <Title level={2} style={{ ...titleStyles, paddingBottom: '8px', fontWeight: '600' }}>
                                {children}
                            </Title>
                        ),
                        h3: ({ children }) => (
                            <Title level={3} style={{ ...titleStyles, borderTop: '2px solid #ededed' }}>
                                {children}
                            </Title>
                        ),
                        h4: ({ children }) => (
                            <Title level={4} style={titleStyles}>
                                {children}
                            </Title>
                        ),
                        blockquote: ({ children }) => (
                            <div style={blockQuoteStyles}>
                                {children}
                            </div>
                        ),
                        p: ({ children }) => (
                            <Paragraph style={{ marginBottom: '16px', lineHeight: '1.7' }}>
                                {children}
                            </Paragraph>
                        ),
                        ul: ({ children }) => (
                            <ul style={{ marginBottom: '16px', paddingLeft: '24px', marginTop: '8px' }}>
                                {children}
                            </ul>
                        ),
                        li: ({ children }) => (
                            <li style={{ marginBottom: '8px', lineHeight: '1.7', listStyleType: 'disc' }}>
                                {children}
                            </li>
                        ),
                    }}
                >
                    {textFileData.content}
                </ReactMarkdown>
            </div>
        );
    }

    return null; // Render nothing if the state is still being determined
};

export default TextViewer;
